import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type { ClientDetail, ClientProfile, Match, Me, ProgramList, Tag } from '../lib/api';
import { navigate } from '../lib/router';
import { daysUntil, fmtDate, fmtDateTime, titleCase } from '../lib/format';
import { fmtK, fmtPct, pct } from '../lib/money';
import { Empty, Kpi, Modal, PageHead, Panel, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { CLIENT_DECISION_LABEL, DECISION_LABEL, DecisionButtons, DecisionPill, MatchFlags, RationaleLine, ScoreBadge } from '../components/matches';
import { AlertList } from '../components/alerts';
import { OPPORTUNITY_TYPES, ProfileForm, SET_ASIDES } from '../components/ProfileForm';
import { STATUS_KIND } from './Clients';

type Tab = 'active' | 'new' | 'relevant' | 'watch' | 'pursue' | 'pending' | 'pass';
const TABS: { id: Tab; label: string }[] = [
  { id: 'active', label: 'Working set' }, { id: 'new', label: 'New' }, { id: 'relevant', label: 'Relevant' }, { id: 'watch', label: 'Monitoring' },
  { id: 'pursue', label: 'Pursuing' }, { id: 'pending', label: 'All undecided' }, { id: 'pass', label: 'Not relevant' },
];
// A client login never sees NTN's undecided pile as such, nor what was set aside.
const CLIENT_TABS = TABS.filter((t) => t.id !== 'pending' && t.id !== 'pass');
const UPDATE_LABEL: Record<string, string> = {
  amended: 'Amended', budget_change: 'Budget', deadline_approaching: 'Deadline', new_solicitation: 'Rule hit', solicitation_amended: 'Amended',
  event_recommended: 'Event', mission_need_matched: 'Mission need',
};
const NOTE_KINDS = ['note', 'client_feedback', 'priority_change', 'relationship', 'teaming', 'strategic_fit', 'follow_up', 'next_step', 'recommendation'];

/**
 * The client dashboard (CPD §3). With clientView the same page is rendered for a client login: no switcher,
 * no editing, no NTN-internal notes, alerts, history or relationships, and opportunity links stay inside
 * the client shell. The API already strips that material; the flag only shapes the page.
 */
export function ClientPage({ id, me, clientView = false }: { id: string; me: Me; clientView?: boolean }) {
  const cv = clientView;
  const [d, setD] = useState<ClientDetail | null>(null);
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState<Tab>('active');
  const [tabItems, setTabItems] = useState<Match[] | null>(null);
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('note');
  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [budget, setBudget] = useState<ProgramList | null>(null);

  const load = useCallback(() => {
    api<ClientDetail>(`/api/clients/${id}`).then((r) => { setD(r); setErr(null); }).catch((e: Error) => setErr(e.message));
    setVersion((v) => v + 1);
  }, [id]);
  useEffect(() => {
    load();
    setTab('active');
    api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([]));
    if (cv) return;
    api<{ items: ClientProfile[] }>('/api/clients').then((r) => setClients(r.items)).catch(() => setClients([]));
    api<ProgramList>(`/api/budget/programs?client=${id}&fit_only=1&sort=fit&limit=10`).then(setBudget).catch(() => setBudget(null));
  }, [load, id, cv]);
  useEffect(() => {
    if (tab === 'active') { setTabItems(null); return; }
    const qs = tab === 'new' ? 'new=1' : `decision=${tab}`;
    api<{ items: Match[] }>(`/api/clients/${id}/matches?${qs}&limit=100`).then((r) => setTabItems(r.items)).catch(() => setTabItems([]));
  }, [tab, id, version]);

  const canEdit = !cv && me.role !== 'viewer';
  const matches = tab === 'active' ? (d?.matches ?? []) : (tabItems ?? []);
  const oppHref = (matchId: string) => (cv ? `#/opportunity/${matchId}` : `#/clients/${id}/opportunity/${matchId}`);
  const open = (m: Match) => { location.hash = oppHref(m.id).slice(1); };
  const tabs = cv ? CLIENT_TABS : TABS;

  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api(`/api/clients/${id}/notes`, { method: 'POST', json: cv ? { body: note.trim() } : { body: note.trim(), kind, visibility } });
      setNote('');
      load();
    } catch (ex) { setErr((ex as Error).message); } finally { setSaving(false); }
  }
  async function ackAll() {
    await api('/api/alerts/ack-all', { method: 'POST', json: { client: id } });
    load();
  }
  async function markReviewed() {
    await api(`/api/clients/${id}/reviewed`, { method: 'POST' });
    load();
  }

  if (err && !d) return <><PageHead title="Client profile" /><Empty>{err === 'not_found' ? 'This profile does not exist or was removed.' : err}</Empty></>;
  if (!d) return <><PageHead title="Loading…" /><Empty>Loading…</Empty></>;

  const switcher = (
    <label className="client-switch">
      <span className="muted sm">Client</span>
      <select className="select" value={id} onChange={(e) => navigate('clients', e.target.value)}>
        {!clients.some((c) => c.id === id) && <option value={id}>{d.name}</option>}
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.new_matches ? ` (${c.new_matches} new)` : ''}</option>)}
      </select>
    </label>
  );
  const lastReview = d.last_reviewed_at ? fmtDateTime(d.last_reviewed_at) : null;

  return (
    <>
      <PageHead
        title={d.name}
        sub={cv ? `Opportunities NTN monitors for ${d.name}${lastReview ? ` · last reviewed ${lastReview}` : ''}` : [d.sector, d.size].filter(Boolean).join(' · ') || 'Client profile'}
        actions={cv ? <Pill kind={STATUS_KIND[d.status]}>{titleCase(d.status)}</Pill> : (
          <>
            {switcher}
            <Pill kind={STATUS_KIND[d.status]}>{titleCase(d.status)}</Pill>
            {canEdit && <button className="ghost-btn" onClick={markReviewed} title={d.last_reviewed_at ? `Last reviewed ${fmtDateTime(d.last_reviewed_at)}${d.last_reviewed_by_name ? ` by ${d.last_reviewed_by_name}` : ''}` : 'Never reviewed'}>Mark reviewed</button>}
            {canEdit && d.counts.alerts_open > 0 && <button className="ghost-btn" onClick={ackAll}>Acknowledge {d.counts.alerts_open} alert{d.counts.alerts_open === 1 ? '' : 's'}</button>}
            {me.role === 'owner' && <button className="ghost-btn" onClick={() => setInviting(true)} title="Create a login that sees only this client's dashboard">Invite client user</button>}
            {canEdit && <button className="primary-btn" onClick={() => setEditing(true)}>Edit profile</button>}
            <a className="ghost-btn" href="#/clients">All profiles</a>
          </>
        )}
      />
      {err && <div className="error-text">{err}</div>}
      <div className="kpi-row">
        <Kpi label={cv ? 'New opportunities' : 'New since review'} value={d.counts.new} hint={cv ? (lastReview ? `since NTN's review on ${fmtDate(d.last_reviewed_at)}` : 'awaiting NTN review') : d.last_reviewed_at ? `fit ≥ 40 · reviewed ${fmtDate(d.last_reviewed_at)}` : 'fit ≥ 40 · never reviewed'} />
        <Kpi label="Relevant" value={d.counts.relevant} />
        <Kpi label="Monitoring" value={d.counts.watch} />
        <Kpi label="Pursuing" value={d.counts.pursue} />
        {!cv && <Kpi label="Undecided ≥ 50" value={d.counts.pending} />}
        {!cv && <Kpi label="Open alerts" value={d.counts.alerts_open} />}
      </div>

      <div className="grid grid-2">
        <Panel title={`New opportunities (${d.new_opportunities.length})`} action={<button className="link" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setTab('new')}>All new →</button>}>
          {d.new_opportunities.length === 0 ? (
            <Empty>{cv ? 'Nothing new since NTN last reviewed your profile. New matches appear after each SAM.gov pull.'
              : d.last_reviewed_at ? `Nothing new since ${fmtDateTime(d.last_reviewed_at)}. New matches appear after each SAM.gov pull.` : 'No undecided opportunities yet. Matches appear after the next ingest, or add technology tags, NAICS codes and keywords to the profile.'}</Empty>
          ) : d.new_opportunities.slice(0, 8).map((m) => <OpportunityLine key={m.id} m={m} tags={tags} canEdit={canEdit} client={cv} onOpen={() => open(m)} onChanged={load} />)}
        </Panel>
        <Panel title={`Priority opportunities (${d.priority.length})`} action={<span className="muted sm">flagged relevant or being pursued</span>}>
          {d.priority.length === 0 ? (
            <Empty>{cv ? 'NTN has not flagged a priority opportunity yet.' : 'Nothing flagged yet. Mark an opportunity Relevant or Pursue and it appears here with its deadline.'}</Empty>
          ) : d.priority.slice(0, 8).map((m) => <OpportunityLine key={m.id} m={m} tags={tags} canEdit={canEdit} client={cv} onOpen={() => open(m)} onChanged={load} />)}
        </Panel>
      </div>

      <div className="grid grid-2">
        <Panel title={`Program updates (${d.updates.length})`} action={<span className="muted sm">last 60 days</span>}>
          {d.updates.length === 0 ? <Empty>No amendments, budget movements or deadline alerts on {cv ? 'your' : "this client's"} opportunities in the last 60 days.</Empty> : d.updates.map((u, i) => (
            <div className="update-row" key={i}>
              <time>{fmtDate(u.at)}</time>
              <span className={`alert-tag ${u.is_new ? 'notable' : 'info'}`} style={{ flexShrink: 0 }}>{UPDATE_LABEL[u.kind] ?? titleCase(u.kind)}</span>
              <span style={{ flex: 1 }}>
                {u.match_id ? <a className="link" href={oppHref(u.match_id)}>{u.title}</a>
                  : cv ? u.title
                  : u.target_type === 'solicitation' && u.target_id ? <a className="link" href={`#/solicitations/id/${u.target_id}`}>{u.title}</a>
                  : u.target_type === 'program' && u.target_id ? <a className="link" href={`#/budget/id/${u.target_id}`}>{u.title}</a>
                  : u.target_type === 'event' && u.target_id ? <a className="link" href={`#/events/id/${u.target_id}`}>{u.title}</a> : u.title}
                {u.detail && <div className="muted sm">{u.detail}</div>}
              </span>
              {u.decision && <DecisionPill decision={u.decision} client={cv} />}
            </div>
          ))}
        </Panel>
        <Panel title="Strategic initiatives" action={cv ? undefined : <a className="link" href="#/budget">Budget & Appropriations →</a>}>
          {d.target_programs.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div className="muted sm" style={{ marginBottom: 6 }}>Programs and initiatives NTN tracks for {cv ? 'you' : d.name}</div>
              <div className="tag-list">{d.target_programs.map((p) => <span key={p} className="tag-toggle on" style={{ cursor: 'default' }}>{p}</span>)}</div>
            </div>
          )}
          {d.initiatives.length === 0 ? (
            <div className="muted sm">{cv ? 'No budget lines flagged for you yet.' : d.target_programs.length ? 'No budget lines scored 50+ or flagged for this client yet.' : 'Add programs and initiatives to the profile, or flag budget lines on the Budget page, and they appear here with their latest figures.'}</div>
          ) : d.initiatives.map((g) => (
            <div className="version" key={g.match_id} style={{ alignItems: 'flex-start' }}>
              <time><ScoreBadge score={g.score} /></time>
              <span style={{ flex: 1 }}>
                {cv ? <strong>{g.pe_number ?? g.line_item} · {g.title}</strong> : <a className="link" href={`#/budget/id/${g.program_id}`}>{g.pe_number ?? g.line_item} · {g.title}</a>}
                <div className="muted sm">{[g.service, g.ba_title ?? g.appropriation, g.latest ? `FY${g.latest.fiscal_year} ${g.latest.stage.toUpperCase()} ${fmtK(g.latest.amount)}` : null].filter(Boolean).join(' · ')}</div>
              </span>
              {g.decision !== 'pending' && <DecisionPill decision={g.decision} client={cv} />}
            </div>
          ))}
          {!cv && d.needs.length > 0 && <div className="muted sm" style={{ marginTop: 10 }}>{d.needs.length} mission need{d.needs.length === 1 ? '' : 's'} fit this client; see below.</div>}
        </Panel>
      </div>

      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-head">
          <h2>Matched opportunities <span className="count">{matches.length}</span></h2>
          <div className="filter-bar" style={{ marginBottom: 0 }}>
            {tabs.map((t) => <button key={t.id} className={`filter-btn ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
          </div>
        </div>
        <div className="panel-body table-wrap">
          {tab !== 'active' && tabItems === null ? <Empty>Loading…</Empty> : matches.length === 0 ? (
            <Empty>{tab === 'active' ? (cv ? 'Nothing scored yet. Matches appear after the next SAM.gov pull.' : 'Nothing scored yet. Matches appear after the next ingest, or add technology tags, NAICS codes and keywords to the profile.') : 'Nothing here.'}</Empty>
          ) : (
            <table className="data-table">
              <thead><tr><th>Fit</th><th>Opportunity</th><th>Type</th><th>Agency</th><th>Source</th><th>Deadline</th><th>Status</th><th>{cv ? 'NTN view' : 'Decision'}</th></tr></thead>
              <tbody>
                {matches.map((m) => {
                  const days = daysUntil(m.response_due_at);
                  return (
                    <tr key={m.id} className="clickable" onClick={() => open(m)}>
                      <td><ScoreBadge score={m.score} /></td>
                      <td>
                        <strong>{m.title}</strong><MatchFlags m={m} />
                        <div className="muted sm">{m.solicitation_number ?? '—'}{m.has_assessment && <span className="tag new" style={{ background: 'rgba(56,189,248,.14)', color: 'var(--cyan)' }}>ASSESSED</span>}</div>
                        <RationaleLine r={m.rationale} tags={tags} />
                      </td>
                      <td>{titleCase(m.notice_type)}</td>
                      <td className="muted">{m.issuing_org_path?.split('.').slice(-1)[0] ?? '—'}</td>
                      <td onClick={(e) => e.stopPropagation()}>{m.url ? <a className="link" href={m.url} target="_blank" rel="noreferrer">{m.channel.toUpperCase()} ↗</a> : <span className="muted">{m.channel.toUpperCase()}</span>}</td>
                      <td>{m.response_due_at ? <>{fmtDate(m.response_due_at)}{days !== null && days >= 0 && <div className={`sm ${days <= 3 ? 'neg' : 'muted'}`}>{days === 0 ? 'today' : `${days} days`}</div>}</> : <span className="muted">No deadline</span>}</td>
                      <td><Pill kind={m.is_open ? 'ok' : 'muted'}>{m.is_open ? 'Open' : 'Closed'}</Pill></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {canEdit ? <DecisionButtons matchId={m.id} decision={m.decision} onChanged={load} /> : <DecisionPill decision={m.decision} client={cv} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {!cv && (
        <Panel title={`Budget lines to watch${budget ? ` · ${budget.summary.fitted} scored` : ''}`} action={budget && <a className="link" href="#/budget">Budget & Appropriations →</a>}>
          {!budget ? <Empty>Loading…</Empty> : budget.items.length === 0 ? (
            <Empty>No budget line scores against this profile yet. Load the R-1 and P-1 exhibits on the Budget page, or add technology tags and capabilities to the profile.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Fit</th><th>Line</th><th>Program</th><th>Service</th><th className="num">FY{budget.current_year} enacted</th><th className="num">FY{budget.budget_year} request</th><th className="num">Δ</th></tr></thead>
                <tbody>
                  {budget.items.map((g) => {
                    const p = pct(g.cy_enacted, g.by_total);
                    return (
                      <tr key={g.id} className="clickable" onClick={() => { location.hash = `/budget/id/${g.id}`; }}>
                        <td><ScoreBadge score={g.fit} /></td>
                        <td className="mono">{g.pe_number ?? g.line_item}</td>
                        <td><strong>{g.title}</strong><div className="muted sm">{g.ba_title ?? g.appropriation}</div></td>
                        <td className="muted">{g.service}</td>
                        <td className="num">{fmtK(g.cy_enacted)}</td>
                        <td className="num"><strong>{fmtK(g.by_total)}</strong></td>
                        <td className={`num ${p !== null && p > 0 ? 'pos' : p !== null && p < 0 ? 'neg' : 'muted'}`}>{g.cy_enacted === null && g.by_total !== null ? 'new' : fmtPct(p) || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      <div className="grid grid-2" style={{ marginTop: 18 }}>
        {!cv && (
          <Panel title={`Mission needs this client fits (${d.needs.length})`} action={<a className="link" href="#/mission-needs">Mission Needs →</a>}>
            {d.needs.length === 0 ? <Empty>No mission need scores against this profile. Needs match on technology domains and required capabilities.</Empty> : d.needs.map((n) => (
              <div className="version" key={n.match_id} style={{ alignItems: 'flex-start' }}>
                <time><ScoreBadge score={n.score} /></time>
                <span style={{ flex: 1 }}><a className="link" href={`#/mission-needs/${n.need_id}`}>{n.title}</a><div className="muted sm">{[n.sponsor_name, n.mission_area, titleCase(n.status), n.role ? `suggested role ${n.role}` : null].filter(Boolean).join(' · ')}</div></span>
                <Pill kind={n.decision === 'team' ? 'ok' : n.decision === 'shortlist' ? 'warn' : 'muted'}>{titleCase(n.decision)}</Pill>
              </div>
            ))}
          </Panel>
        )}
        <Panel title={`Recommended events (${d.events.length})`} action={cv ? undefined : <a className="link" href={`#/events`}>Events →</a>}>
          {d.events.length === 0 ? <Empty>{cv ? 'No upcoming events recommended yet.' : 'No upcoming event scores against this profile yet. Add events on the Events page.'}</Empty> : d.events.map((e) => {
            const dl = daysUntil(e.next_deadline);
            return (
              <div className="version" key={e.match_id} style={{ alignItems: 'flex-start' }}>
                <time><ScoreBadge score={e.score} /></time>
                <span style={{ flex: 1 }}>{cv ? <strong>{e.name}</strong> : <a className="link" href={`#/events/id/${e.event_id}`}>{e.name}</a>}<div className="muted sm">{[e.starts_on ? fmtDate(e.starts_on) : 'date TBD', [e.city, e.state].filter(Boolean).join(', ') || null, e.next_deadline ? `next deadline ${fmtDate(e.next_deadline)}${dl !== null ? ` (${dl}d)` : ''}` : null].filter(Boolean).join(' · ')}</div></span>
                <Pill kind={e.rationale.action === 'Exhibit' || e.rationale.action === 'Team' ? 'ok' : e.rationale.action === 'Monitor' ? 'muted' : 'warn'}>{e.rationale.action}</Pill>
              </div>
            );
          })}
        </Panel>
        {cv && (
          <Panel title={`Notes from NTN and your feedback (${d.notes.length})`}>
            {d.notes.length === 0 && <div className="muted sm">Nothing shared yet. Use the form below to tell NTN about changes in your priorities or feedback on what you are seeing.</div>}
            {d.notes.map((n) => (
              <div className="note" key={n.id}>
                <div><span className="tag new" style={{ marginLeft: 0, marginRight: 8 }}>{titleCase(n.kind).toUpperCase()}</span>{n.body}</div>
                <div className="meta">{n.author ?? 'NTN'} · {fmtDateTime(n.created_at)}</div>
              </div>
            ))}
            <form onSubmit={addNote} style={{ marginTop: 12 }}>
              <div className="field"><label htmlFor="cn-body">Send feedback to NTN</label><textarea id="cn-body" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Changes in priorities, programs to watch, reactions to the opportunities above…" /></div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={saving || !note.trim()}>{saving ? 'Sending…' : 'Send to NTN'}</button></div>
            </form>
          </Panel>
        )}
      </div>

      {!cv && (
        <div className="grid grid-2">
          <Panel title={`Alerts for ${d.name}`}>
            <AlertList items={d.alerts} canAck={canEdit} onChanged={load} showClient={false} emptyText="No open alerts for this account." />
          </Panel>
          <Panel title={`NTN intelligence · notes (${d.notes.length})`}>
            {d.notes.length === 0 && <div className="muted sm">No notes yet. Record client feedback, priority changes, relationships and follow-ups here.</div>}
            {d.notes.map((n) => (
              <div className="note" key={n.id}>
                <div><span className="tag new" style={{ marginLeft: 0, marginRight: 8 }}>{titleCase(n.kind).toUpperCase()}</span>{n.body}</div>
                <div className="meta">{n.author ?? 'Unknown'} · {fmtDateTime(n.created_at)} · {n.visibility === 'client' ? 'visible to client' : 'internal'}</div>
              </div>
            ))}
            {canEdit && (
              <form onSubmit={addNote} style={{ marginTop: 12 }}>
                <div className="form-grid">
                  <div className="field span-2"><label htmlFor="cn-body">Add a note</label><textarea id="cn-body" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Client feedback, changes to priorities, relationships, teaming considerations…" /></div>
                  <div className="field"><label htmlFor="cn-kind">Kind</label>
                    <select id="cn-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
                      {NOTE_KINDS.map((k) => <option key={k} value={k}>{titleCase(k)}</option>)}
                    </select>
                  </div>
                  <div className="field"><label htmlFor="cn-vis">Visibility</label>
                    <select id="cn-vis" className="select" value={visibility} onChange={(e) => setVisibility(e.target.value as 'internal' | 'client')}>
                      <option value="internal">Internal only</option><option value="client">Client-facing</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions"><button className="primary-btn" type="submit" disabled={saving || !note.trim()}>{saving ? 'Saving…' : 'Save note'}</button></div>
              </form>
            )}
          </Panel>
        </div>
      )}

      <div className="grid grid-2">
        <Panel title={cv ? 'Your profile as NTN monitors it' : 'Profile: capabilities, priorities, opportunity criteria'}>
          <dl className="kv kv-wide">
            <dt>Overview</dt><dd>{d.summary ?? '—'}</dd>
            <dt>Government work</dt><dd>{d.government_work ?? '—'}</dd>
            <dt>Capabilities</dt><dd>{d.capabilities.join(', ') || '—'}</dd>
            <dt>Technology</dt><dd>{d.technology_tags.length ? <TagChips tags={d.technology_tags} all={tags} max={12} /> : '—'}</dd>
            <dt>NAICS</dt><dd>{d.naics_codes.join(', ') || '—'}</dd>
            <dt>Target agencies</dt><dd>{d.target_agencies.join(', ') || '—'}</dd>
            <dt>Programs tracked</dt><dd>{d.target_programs.join(', ') || '—'}</dd>
            <dt>Markets</dt><dd>{d.markets.join(', ') || '—'}</dd>
            <dt>Priorities</dt><dd>{d.strategic_priorities ?? '—'}</dd>
            <dt>BD objectives</dt><dd>{d.bd_objectives ?? '—'}</dd>
            <dt>Notice types</dt><dd>{d.opportunity_types.length ? d.opportunity_types.map((t) => OPPORTUNITY_TYPES.find((x) => x.id === t)?.label ?? titleCase(t)).join(', ') : 'All pursuable types'}</dd>
            <dt>Keywords</dt><dd>{d.monitor_keywords.join(', ') || '—'}</dd>
            <dt>Funding interests</dt><dd>{d.funding_interests.join(', ') || '—'}</dd>
            <dt>Partnerships</dt><dd>{d.partnership_interests ?? '—'}</dd>
            {!cv && <><dt>Relationships</dt><dd>{d.relationships ?? '—'}</dd></>}
            <dt>Set-asides held</dt><dd>{d.set_asides.length ? d.set_asides.map((s) => SET_ASIDES.find((x) => x.id === s)?.label ?? s).join(', ') : 'None'}</dd>
            <dt>Vehicles</dt><dd>{d.contract_vehicles.join(', ') || '—'}</dd>
            <dt>Certifications</dt><dd>{d.certifications.join(', ') || '—'}</dd>
          </dl>
          {cv && <div className="muted sm" style={{ marginTop: 10 }}>To change what NTN monitors for you, send feedback above or contact your NTN analyst.</div>}
        </Panel>
        {!cv && (
          <Panel title={`Change history (${d.history.length})`}>
            {d.history.length === 0 && <div className="muted sm">No changes recorded.</div>}
            {d.history.map((h, i) => (
              <div className="version" key={i}><time>{fmtDateTime(h.changed_at)}</time><span>{titleCase(h.action)}{h.actor ? ` by ${h.actor}` : ' by system'}</span></div>
            ))}
            <div className="muted sm" style={{ marginTop: 10 }}>Created {fmtDateTime(d.created_at)} · every edit is kept in the audit log.</div>
          </Panel>
        )}
      </div>

      {editing && <ProfileForm tags={tags} existing={d} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {inviting && <InviteClientUser clientId={id} clientName={d.name} onClose={() => setInviting(false)} />}
    </>
  );
}

/** One opportunity in the New / Priority panels: fit, title with flags, type · agency · deadline, decision. */
function OpportunityLine({ m, tags, canEdit, client, onOpen, onChanged }: { m: Match; tags: Tag[]; canEdit: boolean; client: boolean; onOpen: () => void; onChanged: () => void }) {
  const days = daysUntil(m.response_due_at);
  const meta: ReactNode[] = [titleCase(m.notice_type), m.issuing_org_path?.split('.').slice(-1)[0] ?? null,
    m.response_due_at ? `due ${fmtDate(m.response_due_at)}${days !== null && days >= 0 ? ` (${days}d)` : ''}` : 'no deadline'].filter(Boolean);
  const labels = client ? CLIENT_DECISION_LABEL : DECISION_LABEL;
  return (
    <div className="version" style={{ alignItems: 'flex-start' }}>
      <time><ScoreBadge score={m.score} /></time>
      <span style={{ flex: 1, minWidth: 0 }}>
        <a className="link" href="#" onClick={(e) => { e.preventDefault(); onOpen(); }}>{m.title}</a><MatchFlags m={m} />
        <div className="muted sm">{meta.join(' · ')}</div>
        <RationaleLine r={m.rationale} tags={tags} />
      </span>
      {canEdit ? <DecisionButtons matchId={m.id} decision={m.decision} onChanged={onChanged} /> : m.decision !== 'pending' && <span className="muted sm">{labels[m.decision]}</span>}
    </div>
  );
}

/** Owner-only: create a login that sees only this client's dashboard. Shows the temporary password once. */
function InviteClientUser({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ email: string; password: string | null } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api<{ email: string; temp_password?: string; existing_account: boolean }>('/api/admin/users', {
        method: 'POST', json: { email: email.trim(), display_name: name.trim() || undefined, role: 'client', client_profile_id: clientId },
      });
      setDone({ email: r.email, password: r.temp_password ?? null });
    } catch (ex) {
      setErr(ex instanceof ApiError && ex.status === 409 ? 'That person is already a member of this workspace. Manage their role on the Admin page.' : (ex as Error).message);
    } finally { setBusy(false); }
  }

  return (
    <Modal title={`Invite a ${clientName} user`} onClose={onClose}>
      {done ? (
        <div className="reveal">
          {done.password
            ? <>Temporary password for <strong>{done.email}</strong>: <strong>{done.password}</strong><br /><span className="muted">Shown once. They must change it at first sign-in. Pass it on securely; Praxis does not send email.</span></>
            : <>{done.email} already had a Praxis account and can now sign in to see {clientName}'s dashboard with their existing password.</>}
          <div style={{ marginTop: 8 }}><button className="ghost-btn" onClick={onClose}>Close</button></div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="muted sm" style={{ marginBottom: 12 }}>
            This login sees only {clientName}'s dashboard and opportunities: NTN's assessments, statuses and client-facing notes. Internal notes, relationships, other clients and opportunities marked Not relevant stay hidden.
          </div>
          <div className="form-grid">
            <div className="field span-2"><label htmlFor="ic-email">Email</label><input id="ic-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div className="field span-2"><label htmlFor="ic-name">Display name</label><input id="ic-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Who at the client this is for" /></div>
          </div>
          {err && <div className="error-text">{err}</div>}
          <div className="form-actions">
            <button type="button" className="ghost-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-btn" disabled={busy || !email.trim()}>{busy ? 'Creating…' : 'Create client login'}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}
